import { Course } from '@/components/CourseCard';
import HomeCourseCard from '@/components/HomeCourseCard';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import useInertiaData from '@/hooks/useInertiaData';
import AppLayout from '@/layouts/AppLayout';
import { Link } from '@inertiajs/react';
import { GraduationCap, Layers } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import ContinueLearning from './Partials/ContinueLearning';

interface GroupItem {
    item_type: 'group';
    id: number;
    title: string;
    image?: string | null;
    order: number;
    total_topics: number;
    total_classes: number;
    completed_classes: number;
    progress_percentage: number;
}

type CategoryItem = (Course & { item_type: 'course' }) | GroupItem;

interface Category {
    id: number;
    name: string;
    items: CategoryItem[];
}

interface Props {
    categories_with_courses?: Category[];
}

const HomeGroupCard: React.FC<{ item: GroupItem }> = ({ item }) => {
    const { t } = useTranslation();
    const { account_paused } = useInertiaData();
    const progressPercentage = item.progress_percentage ?? 0;

    const cardColors = [
        'bg-emerald-500',
        'bg-blue-500',
        'bg-purple-500',
        'bg-orange-500',
        'bg-pink-500',
        'bg-indigo-500',
    ];
    const colorClass = cardColors[item.id % cardColors.length];

    const cardContent = (
        <div className="space-y-4">
            <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1 space-y-2">
                    <h3 className="course-title">
                        {item.title}
                    </h3>
                </div>
                <div className={`h-10 w-10 sm:h-12 sm:w-12 ${colorClass} ml-4 flex flex-shrink-0 items-center justify-center rounded-lg`}>
                    <Layers size={20} className="text-white sm:h-6 sm:w-6" />
                </div>
            </div>

            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">
                        {progressPercentage}{t('home.percentCompleted')}
                    </span>
                    <span className="text-sm text-gray-500">
                        {progressPercentage}%
                    </span>
                </div>
                <div className="h-2 w-full rounded-full bg-gray-200">
                    <div
                        className={`h-2 ${colorClass} rounded-full transition-all duration-500`}
                        style={{ width: `${progressPercentage}%` }}
                    ></div>
                </div>
            </div>
        </div>
    );

    if (account_paused) {
        return (
            <div className="course-card block cursor-not-allowed grayscale opacity-60">
                {cardContent}
            </div>
        );
    }

    return (
        <Link href={`/courses/groups/${item.id}/`} className="group course-card block">
            {cardContent}
        </Link>
    );
};

const HomeContent: React.FC<Props> = ({ categories_with_courses }) => {
    const { t } = useTranslation();

    const hasNoCourses =
        !categories_with_courses || categories_with_courses.length === 0;

    return (
        <AppLayout>
            <PageTitle title="Inicio" />
            <div className="mx-auto max-w-7xl space-y-6 lg:space-y-8">
                {/* Continue Learning Section */}
                <ContinueLearning />

                {hasNoCourses ? (
                    <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
                        <div className="py-12 text-center">
                            <GraduationCap className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                            <h3 className="mb-2 text-xl font-semibold text-gray-900">
                                {t('home.noCoursesEnrolled')}
                            </h3>
                            <p className="text-gray-600">
                                {t('home.exploreCatalog')}
                            </p>
                        </div>
                    </div>
                ) : (
                    categories_with_courses?.map((category) => (
                        <div key={category.id} className="space-y-6">
                            <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">
                                {category.name}
                            </h2>
                            <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
                                {category.items.map((item) => {
                                    if (item.item_type === 'group') {
                                        return (
                                            <HomeGroupCard
                                                key={`group_${item.id}`}
                                                item={item}
                                            />
                                        );
                                    }
                                    return (
                                        <HomeCourseCard
                                            key={`course_${item.id}`}
                                            course={item}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </AppLayout>
    );
};

const Home: React.FC<Props> = ({ categories_with_courses }) => {
    return (
        <ThemeWrapper>
            <HomeContent categories_with_courses={categories_with_courses} />
        </ThemeWrapper>
    );
};

export default Home;
